
import { useState, useEffect } from 'react';

export default function GallerySection() {
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(0);

  const galleryItems = [
    {
      src: "/images/galeria1.jpg",
      alt: "Evento A Laxe"
    },
    {
      src: "/images/galeria2.jpg",
      alt: "Evento Bicos de Papel"
    },
    {
      src: "/images/galeria3.jpg",
      alt: "Evento con Señor"
    },
    {
      src: "/images/galeria4.jpg",
      alt: "Evento con público"
    },
    {
      src: "/images/galeria5.jpg",
      alt: "Evento con niño"
    },
    {
      src: "/images/galeria6.jpg",
      alt: "Evento para niños"
    },
    {
      src: "/images/galeria7.jpg",
      alt: "Evento para familia"
    },
    {
      src: "/images/galeria8.jpg",
      alt: "Evento en Vigo"
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % galleryItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [galleryItems.length]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryItems.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  const goToImage = (index: number) => {
    setCurrentImage(index);
  };

  const openLightbox = (index: number) => {
    setLightboxImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextLightboxImage = () => {
    setLightboxImage((prev) => (prev + 1) % galleryItems.length);
  };

  const prevLightboxImage = () => {
    setLightboxImage((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  // Close lightbox on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    if (lightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen]);

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <h2 className="font-elegant text-5xl md:text-6xl font-bold text-white mb-4 relative">
              Galería <span className="text-transparent bg-gradient-to-r from-red-500 to-gold bg-clip-text">Mágica</span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-red-500 to-gold rounded-full"></div>
            </h2>
          </div>
          
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed">
            Contempla la maravilla y el asombro capturados en estos momentos de mis actuaciones. Cada imagen cuenta una historia de lo imposible hecho posible.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-6xl mx-auto">
          {/* Main Image */}
          <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gray-800">
            <div 
              className="group cursor-pointer"
              onClick={() => openLightbox(currentImage)}
            >
              <img 
                src={galleryItems[currentImage].src}
                alt={galleryItems[currentImage].alt}
                className="w-full h-[500px] object-cover object-top transition-all duration-500"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              
              {/* Hover Hand Icon - Only shows on image hover */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                <div className="transform scale-75 group-hover:scale-100 transition-transform duration-300">
                  <i className="ri-hand-heart-line text-6xl text-white drop-shadow-lg"></i>
                  <p className="text-white text-lg font-medium mt-2 text-center">Haz clic para ampliar</p>
                </div>
              </div>
            </div>

            {/* Navigation Arrows - Outside the hover group */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-110 z-10"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-110 z-10"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>

          {/* Thumbnails */}
          <div className="flex justify-center mt-6 space-x-3 overflow-x-auto pb-2">
            {galleryItems.map((item, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                  currentImage === index 
                    ? 'ring-2 ring-red-500 scale-110' 
                    : 'ring-1 ring-gray-600 hover:ring-red-400 opacity-70 hover:opacity-100'
                }`}
              >
                <img 
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover object-top"
                />
              </button>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-6 space-x-2">
            {galleryItems.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentImage === index 
                    ? 'bg-red-500 scale-125' 
                    : 'bg-gray-600 hover:bg-red-400'
                }`}
              />
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-4 bg-gray-700 rounded-full h-1 max-w-md mx-auto">
            <div 
              className="bg-gradient-to-r from-red-500 to-gold h-full rounded-full transition-all duration-500"
              style={{ width: `${((currentImage + 1) / galleryItems.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
             onClick={closeLightbox}>
          <div className="relative max-w-6xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 bg-black/70 hover:bg-black/90 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-110"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevLightboxImage();
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full backdrop-blur-sm transition-all duration-30 hover:scale-110"
            >
              <i className="ri-arrow-left-line text-2xl"></i>
            </button>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextLightboxImage();
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full backdrop-blur-sm transition-all duration-30 hover:scale-110"
            >
              <i className="ri-arrow-right-line text-2xl"></i>
            </button>

            {/* Lightbox Image */}
            <div className="relative max-w-full max-h-full flex items-center justify-center"
                 onClick={(e) => e.stopPropagation()}>
              <img
                src={galleryItems[lightboxImage].src}
                alt={galleryItems[lightboxImage].alt}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              
              {/* Image Counter */}
              <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm">
                {lightboxImage + 1} / {galleryItems.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
